import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEOHead from '../common/SEOHead';
import SafeIcon from '../common/SafeIcon';

const {FiHome, FiUsers, FiTool, FiDollarSign, FiShield, FiCheck, FiTrendingUp, FiCalendar, FiAward, FiClock} = FiIcons;

const PropertyManagementPage = () => {
  const services = [
    {
      icon: FiUsers,
      title: 'Comprehensive Tenant Management',
      description: 'Complete tenant lifecycle management from screening to move-out, ensuring quality occupants and smooth transitions.',
      features: ['Thorough tenant screening', 'Lease agreement preparation', 'Move-in/move-out coordination', 'Tenant relationship management']
    },
    {
      icon: FiDollarSign,
      title: 'Financial Oversight',
      description: 'Professional financial management including rent collection, expense tracking, and detailed reporting for complete transparency.',
      features: ['Timely rent collection', 'Expense management', 'Monthly financial statements', 'Tax document preparation']
    },
    {
      icon: FiTool,
      title: 'Property Maintenance Excellence',
      description: 'Proactive maintenance coordination with trusted contractors to preserve property value and tenant satisfaction.',
      features: ['24/7 emergency response', 'Preventive maintenance schedules', 'Quality contractor network', 'Cost-effective repairs']
    },
    {
      icon: FiShield,
      title: 'Legal Compliance & Protection',
      description: 'Full compliance with Alberta Residential Tenancies Act and Calgary bylaws to protect your investment interests.',
      features: ['Legal compliance monitoring', 'Eviction assistance', 'Dispute resolution', 'Insurance coordination']
    }
  ];

  const benefits = [
    '15-25% Higher Rental Income',
    '50% Fewer Vacancy Days',
    '98% Tenant Satisfaction Rate',
    '24/7 Emergency Support',
    'Monthly Financial Reports',
    'Professional Property Inspections'
  ];

  const process = [
    { step: '1', title: 'Property Assessment', description: 'Comprehensive evaluation of your property and market analysis to determine optimal rental strategy.' },
    { step: '2', title: 'Custom Management Plan', description: 'Tailored management approach based on your property type, location, and investment goals.' },
    { step: '3', title: 'Implementation', description: 'Professional tenant placement, maintenance setup, and systems implementation for seamless operations.' },
    { step: '4', title: 'Ongoing Management', description: 'Continuous oversight, tenant communication, and performance optimization to maximize returns.' }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Property Management Services Calgary",
    "description": "Professional property management services in Calgary offering comprehensive tenant management, financial oversight, maintenance coordination, and legal compliance.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "DGM Property Management",
      "telephone": "587-800-1215",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Calgary",
        "addressRegion": "Alberta",
        "addressCountry": "CA"
      }
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "51.0447",
        "longitude": "-114.0719"
      },
      "geoRadius": "50000"
    },
    "serviceType": ["Property Management", "Tenant Management", "Financial Management", "Maintenance Coordination", "Legal Compliance"]
  };

  return (
    <>
      <SEOHead
        title="Property Management Services Calgary | Complete Management Solutions"
        description="DGM Property Management offers comprehensive property management in Calgary. Professional tenant placement, financial oversight, maintenance, and legal compliance. Call 587-800-1215."
        keywords="property management Calgary, Calgary property manager, complete property management, residential property management, commercial property management, property oversight Calgary"
        canonical="/services/property-management"
        ogImage="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630"
        schemaData={schemaData}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Property Management Services Calgary
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Complete property management solutions that maximize your investment returns while providing peace of mind. 
                Professional oversight for residential and commercial properties across Calgary and Airdrie.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Main Services Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Management Services</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                End-to-end property management solutions designed specifically for Calgary's dynamic real estate market.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={service.icon} className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                      <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                      <div className="space-y-2">
                        {service.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center gap-2">
                            <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Management Process</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                A structured approach to property management that ensures consistent results and client satisfaction.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-4 gap-8">
              {process.map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl font-bold text-gray-900 mb-8">Why Choose Our Property Management?</h2>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={benefit}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <SafeIcon icon={FiAward} className="w-6 h-6 text-blue-600 flex-shrink-0" />
                      <span className="text-lg text-gray-700">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <div className="relative overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"
                    alt="Professional property management services in Calgary"
                    className="w-full h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold mb-4">Ready to Maximize Your Property Investment?</h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Join hundreds of Calgary property owners who trust DGM Property Management for professional, 
                reliable service that delivers results.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Get Free Property Analysis
                </motion.a>
                <motion.a
                  href="tel:587-800-1215"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Call 587-800-1215
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PropertyManagementPage;